import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  Flex
} from '@homebotapp/hb-react-component-catalog'
import { useState } from 'react'
import { verifyTeamMembership } from '../../../store/slices/homebotNetwork/clientTeamMemberships'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../store'
import { defineMessages, useIntl } from 'react-intl'

export const MSG = defineMessages({
  confirmed: {
    id: 'ConfirmPreferredAgentModal.confirmed',
    defaultMessage: 'Agent confirmed successfully'
  },
  failed: {
    id: 'ConfirmPreferredAgentModal.failed',
    defaultMessage: 'Failed to confirm agent. Please try again.'
  },
  heading: {
    id: 'ConfirmPreferredAgentModal.heading',
    defaultMessage: 'Confirm to keep this agent on your Homebot Team'
  },
  body: {
    id: 'ConfirmPreferredAgentModal.body',
    defaultMessage:
      'By clicking "Confirm" you authorize HB Real Estate Services, Inc. and Homebot, Inc. to share your contact information (name and email) and any details you include in direct messages with the agent above. The agent may contact you about your inquiry and will be included in direct messages related to real estate. You can remove or replace them anytime.'
  },
  cancel: {
    id: 'ConfirmPreferredAgentModal.cancel',
    defaultMessage: 'Cancel'
  },
  confirm: {
    id: 'ConfirmPreferredAgentModal.confirm',
    defaultMessage: 'Confirm'
  }
})

interface ConfirmPreferredAgentModalProps {
  isOpen: boolean
  onClose: () => void
  membershipId?: string
  clientId?: string
}

export const ConfirmPreferredAgentModal = ({
  isOpen,
  onClose,
  membershipId,
  clientId
}: ConfirmPreferredAgentModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const toast = useToast()
  const dispatch = useDispatch<AppDispatch>()
  const intl = useIntl()

  const handleConfirm = async () => {
    if (!membershipId || !clientId) return

    setIsSubmitting(true)
    try {
      const action = await dispatch(
        verifyTeamMembership({
          clientId,
          clientTeamMembershipId: membershipId
        })
      )

      if (action.meta.requestStatus === 'fulfilled') {
        toast({
          title: 'Success',
          description: `${intl.formatMessage(MSG.confirmed)}`,
          status: 'success'
        })

        onClose()
      } else {
        throw new Error(action.payload)
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: `${intl.formatMessage(MSG.failed)}`,
        status: 'error'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text size='lg' fontWeight='semibold' color='neutral.900'>
            {intl.formatMessage(MSG.heading)}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb='4'>
          <Text color='neutral.800'>{intl.formatMessage(MSG.body)}</Text>
          <Flex justify='right' mt='4' gap={2}>
            <Button
              colorScheme='secondary'
              variant='solid'
              onClick={onClose}
              tracking={{
                guid: 'oqZxSJ3oFPxjaRoTRTjKx4',
                ui_context: 'Cancel confirm preferred agent modal',
                descriptor: 'Cancel confirm preferred agent modal'
              }}
            >
              {intl.formatMessage(MSG.cancel)}
            </Button>
            <Button
              colorScheme='primary'
              onClick={handleConfirm}
              isLoading={isSubmitting}
              tracking={{
                guid: 'ttyFF7gzMUTsxo2qyaqUuF',
                ui_context: 'Confirm preferred agent modal',
                descriptor: 'Confirm preferred agent modal'
              }}
            >
              {intl.formatMessage(MSG.confirm)}
            </Button>
          </Flex>
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
